import { Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import styles from "./leaselist.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { ChevronDown, ChevronUp, Code, User } from "react-feather";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader, TableLoaderSmall } from "../../../../components/LoaderC";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const LeaseList = ({ filterData, selectedIds, setSelectedIds }) => {
  const redirect = useHistory();
  const { getAccessTokenSilently } = useAuth0();
  const [filterredData, setFilterredData] = useState([]);
  const [allAptIds, setAllAptIds] = useState([]);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(filterData?.searchKey || "");

  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const selectionKeyRef = useRef(null);

  const property = filterData?.property;
  const apartment = filterData?.apartment;
  const status = filterData?.status;

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  };

  const fetchLeases = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
    if (isActiveFilter(property)) params.property_id = property;
    if (isActiveFilter(apartment)) params.apt_id = apartment;
    if (isActiveFilter(status)) params.status = status;
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/leases-paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setFilterredData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));

      if (replace) {
        const aptIds = response.data?.apt_ids || [];
        setAllAptIds(aptIds);

        // Select every matching unit when search/filters change (same as before); keep manual selection on sort change
        const selectionKey = JSON.stringify([debouncedSearch, property, apartment, status]);
        if (selectionKeyRef.current !== selectionKey) {
          selectionKeyRef.current = selectionKey;
          setSelectedIds(aptIds);
        }
      }

      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Leases] Failed to load lease list", error);
      toast.error(error?.response?.data?.message || "Unable to load leases. Please try again.");

      if (replace) {
        setLoadError(true);
        setFilterredData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, property, apartment, status, sortItem, sortOrder, setSelectedIds]);

  // Debounce search text input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterData?.searchKey || "");
    }, 400);

    return () => clearTimeout(handler);
  }, [filterData?.searchKey]);

  // Load the first chunk again when search, filters or sorting change
  useEffect(() => {
    fetchLeases(0, true);
  }, [fetchLeases]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchLeases(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchLeases]);

  const goToDetails = (item) => {
    redirect.push("/leases/details/" + item.tenant_id);
  }

  if (contentLoading) {
    return (<div className="Loader TableLoader">
      <TableLoader />
    </div>);
  }

  const rowKey = (item) => (item?.list_id ? "lease-" + item.list_id : "apt-" + item?.apt_id);

  return (<div className={refetching ? `${styles.IncomeListUnderSec} ${styles.Refetching}` : `${styles.IncomeListUnderSec}`}>
    <div className={`${styles.ListCardDivSec}`}>
      {filterredData.map((item) => {
        return (<div key={rowKey(item)} className={`${styles.CardDivList}`}>
          <div className={`${styles.FirstSec} ${styles.ProfileImg}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (item?.tenant_id) { redirect.push("/people/tenants/details/" + item.tenant_id); } }}>
            {item?.tenant_id ? (<>
              {item?.tenant_image !== "" ? (<img src={item?.tenant_image} alt="" />) : (<div className={`${styles.ProNameIMG}`}>
                <p>{item?.tenant_initial}</p>
              </div>)}
            </>) : (<div className={`${styles.ProNameIMG}`}>
              <User />
            </div>)}
            <div>
              {item?.tenant_id ? <p className={`${styles.Name} ${styles.Hover}`}>{item?.tenant_name}</p> : <p className={`${styles.Name}`}>Vacant</p>}
              <p className={`${styles.Name} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>{item.property_name} - {item.apt_no}</p>
            </div>
          </div>
          <div className={`${styles.SecondSec}`}>
            <p className={`${styles.Name}`}>Rent: <span>${item.formatted_rent}</span></p>
            <p className={`${styles.Name}`}>Sec. Deposit: <span>{item?.tenant_id ? "$" + item.formatted_amount : "-"}</span></p>
          </div>
          <div className={`${styles.ThirdSec}`}>
            <div className={`${styles.StatusBadgeSecMobile}`}>
              {item?.tenant_id ? (<>
                {item.lease_status.indexOf(1) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Verified</span>)}
                {item.lease_status.indexOf(0) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.NotVerified}`}>Not Verified</span>)}
                {item.lease_status.indexOf(4) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Signed</span>)}
                {item.lease_status.indexOf(3) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Tenant Signed</span>)}
                {item.lease_status.indexOf(2) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.NotVerified}`}>Not Signed</span>)}
                {item.lease_status.indexOf(5) > -1 && (<span className={`${styles.StatusBadge} ${styles.Pending}`}>Expire Soon</span>)}
                {item.lease_status.indexOf(6) > -1 && (<span className={`${styles.StatusBadge} ${styles.Failed}`}>Expired</span>)}
              </>) : ("-")}
            </div>
            <p className={`${styles.Name}`}>
              <span>{item?.tenant_id ? dayjs(item.start_date).format("L") + " - " + dayjs(item.end_date).format("L") : "-"}</span>
            </p>
          </div>
        </div>);
      })}
    </div>

    {filterredData?.length > 0 && (<TableContainer className={`${styles.TableDiv} CustomTableDiv`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={`${styles.TableHead} ${styles.WithCheckBox}`} style={{ width: 300 }} onClick={(e) => changeSortOrder("tenant_name")}>
              <Checkbox
                indeterminate={selectedIds?.length > 0 && allAptIds?.length !== selectedIds?.length}
                checked={allAptIds?.length > 0 && allAptIds?.length === selectedIds?.length}
                onClick={(e) => { e.stopPropagation(); }}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedIds(allAptIds);
                  } else {
                    setSelectedIds([]);
                  }
                }}
              />
              Tenant
              {sortItem === "tenant_name" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "tenant_name" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "tenant_name" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("rent")}>
              Rent
              {sortItem === "rent" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "rent" && sortOrder === "desc" && <ChevronUp />}
              {sortItem !== "rent" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("security_amount")}>
              Sec. Deposit
              {sortItem === "security_amount" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "security_amount" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "security_amount" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 300 }} onClick={(e) => changeSortOrder("lease_term")}>
              Lease Term
              {sortItem === "lease_term" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "lease_term" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "lease_term" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 200 }} onClick={(e) => changeSortOrder("property_name")}>
              Property
              {sortItem === "property_name" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "property_name" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "property_name" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("apt_no")}>
              Unit
              {sortItem === "apt_no" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "apt_no" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "apt_no" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 200 }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterredData.map((item) => {
            return (<TableRow key={rowKey(item)} onClick={(e) => goToDetails(item)}>
              <TableCell className={`${styles.TableBodyText} ${styles.ProfileImg} ${styles.Hover} ${styles.WithCheckBox}`}>
                {item?.tenant_id ? (<>
                  <div className={`${styles.TenantInside}`}>
                    <Checkbox
                      checked={selectedIds?.indexOf(parseInt(item?.apt_id)) > -1}
                      value={item?.apt_id}
                      onClick={(e) => { e.stopPropagation(); }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (selectedIds?.indexOf(parseInt(item?.apt_id)) === -1)
                            setSelectedIds(prev => {
                              return prev.concat([parseInt(item?.apt_id)]);
                            })
                        } else {
                          setSelectedIds(prev => {
                            return prev.filter(i => i !== item?.apt_id)
                          })
                        }
                      }}
                    />
                    {item?.tenant_image !== "" ? (<img src={item?.tenant_image} alt="" />) : (<div className={`${styles.ProNameIMG}`}>
                      <p>{item?.tenant_initial}</p>
                    </div>)}
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (item?.tenant_id) {
                          redirect.push("/people/tenants/details/" + item.tenant_id);
                        }
                      }}
                    >{item?.tenant_name}</span>
                  </div>
                </>) : (<div className={`${styles.TenantInside} ${styles.WithOutCheckBox}`}>
                  <Checkbox
                    checked={selectedIds?.indexOf(parseInt(item?.apt_id)) > -1}
                    value={item?.apt_id}
                    onClick={(e) => { e.stopPropagation(); }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        if (selectedIds?.indexOf(parseInt(item?.apt_id)) === -1)
                          setSelectedIds(prev => {
                            return prev.concat([parseInt(item?.apt_id)]);
                          })
                      } else {
                        setSelectedIds(prev => {
                          return prev.filter(i => i !== item?.apt_id)
                        })
                      }
                    }}
                  />
                  <div className={`${styles.ProNameIMG}`}>
                    <User />
                  </div>{" "}
                  Vacant
                </div>
                )}
              </TableCell>
              <TableCell className={`${styles.TableBodyText}`}>${item.formatted_rent}</TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item?.tenant_id ? "$" + item.formatted_amount : "-"}</TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item?.tenant_id ? dayjs(item.start_date).format("L") + " - " + dayjs(item.end_date).format("L") : "-"}</TableCell>
              <TableCell className={`${styles.TableBodyText} ${styles.Hover}`}>
                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>{item.property_name}</span>
              </TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item.apt_no}</TableCell>
              <TableCell>
                {item?.tenant_id ? (<>
                  {item.lease_status.indexOf(1) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Verified</span>)}
                  {item.lease_status.indexOf(0) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.NotVerified}`}>Not Verified</span>)}
                  {item.lease_status.indexOf(4) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Signed</span>)}
                  {item.lease_status.indexOf(3) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Tenant Signed</span>)}
                  {item.lease_status.indexOf(2) > -1 && item.lease_status.indexOf(6) === -1 && (<span className={`${styles.StatusBadge} ${styles.NotVerified}`}>Not Signed</span>)}
                  {item.lease_status.indexOf(5) > -1 && (<span className={`${styles.StatusBadge} ${styles.Pending}`}>Expire Soon</span>)}
                  {item.lease_status.indexOf(6) > -1 && (<span className={`${styles.StatusBadge} ${styles.Failed}`}>Expired</span>)}
                  {item.lease_status.indexOf(7) > -1 && (<span className={`${styles.StatusBadge} ${styles.Archived}`}>Archived</span>)}
                </>) : ("-")}
              </TableCell>
            </TableRow>);
          })}
        </TableBody>
      </Table>
    </TableContainer>)}

    {/* Sentinel: entering the viewport loads the next chunk */}
    <div ref={sentinelRef} className={`${styles.ScrollSentinel}`} />

    {loadingMore && (<div className={`${styles.ScrollStatus}`}>
      <TableLoaderSmall />
    </div>)}

    {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
      <p>Couldn't load more leases.</p>
      <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchLeases(offset, false)}>Retry</button>
    </div>)}

    {loadError && (<div className={`${styles.NoDataMain}`}>
      <p className={`${styles.NoDataText}`}>Something went wrong!</p>
      <p className={`${styles.NoDataTextSub}`}>we couldn't load the leases.</p>
      <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchLeases(0, true)}>Retry</button>
    </div>)}

    {!loadError && filterredData.length === 0 && (<div className={`${styles.NoDataMain}`}>
      <div className={`${styles.NoDataIMG}`}>
        <img src="/static/img/no-lease.png" alt="No Data" />
      </div>
      <p className={`${styles.NoDataText}`}>Unfortunately!</p>
      <p className={`${styles.NoDataTextSub}`}>we couldn't find any leases at this time.</p>
    </div>)}
  </div>);
}

export default LeaseList;